import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, LogOut, Package } from "lucide-react";
import { fmt } from "@/data/packs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "oz_admin_token";

const PROVIDER_LABEL = { stripe: "Carte (Stripe)", cic: "Carte (CIC)", paypal: "PayPal" };
const STATUS_STYLE = {
  paid: "bg-green-100 text-green-700",
  pending: "bg-amber-100 text-amber-700",
  payment_processing: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700",
  cancelled: "bg-gray-200 text-gray-600",
  refunded: "bg-blue-100 text-blue-700",
};
const STATUS_LABEL = {
  paid: "Payée", pending: "En attente", payment_processing: "En traitement",
  failed: "Échouée", cancelled: "Annulée", refunded: "Remboursée",
};

export default function AdminOrders() {
  const [token, setToken] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("all");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const load = useCallback(async (tk, status) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/orders?status=${status}`, {
        headers: { "X-Admin-Token": tk },
      });
      setOrders(data);
      setAuthed(true);
      localStorage.setItem(LS_KEY, tk);
    } catch (e) {
      if (e.response?.status === 401) {
        setError("Token administrateur invalide.");
        setAuthed(false);
        localStorage.removeItem(LS_KEY);
      } else {
        setError("Erreur de chargement.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const connect = (e) => {
    e.preventDefault();
    setToken(input.trim());
    load(input.trim(), tab);
  };

  const logout = () => {
    localStorage.removeItem(LS_KEY);
    setToken("");
    setAuthed(false);
    setOrders([]);
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-6 h-6 text-[var(--oz-gold)]" />
          <h1 className="font-serif text-2xl text-[var(--oz-text)]">Commandes</h1>
        </div>
        <form onSubmit={connect} className="bg-white border border-[var(--oz-border)] rounded-2xl p-6 space-y-4">
          <label className="block text-sm text-[var(--oz-muted)]">Token administrateur</label>
          <input
            data-testid="admin-token-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Collez votre token admin"
            className="w-full rounded-xl border border-[var(--oz-border)] px-4 py-3 text-sm outline-none focus:border-[var(--oz-gold)]"
          />
          {error && <p data-testid="admin-error" className="text-sm text-red-600">{error}</p>}
          <button
            data-testid="admin-connect"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm hover:bg-black transition-colors"
          >
            Se connecter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 md:py-16">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Package className="w-6 h-6 text-[var(--oz-gold)]" />
          <h1 className="font-serif text-2xl text-[var(--oz-text)]">Commandes</h1>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)]">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        {["all", "paid", "pending"].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              tab === t ? "bg-[var(--oz-dark)] text-white" : "bg-white border border-[var(--oz-border)] text-[var(--oz-muted)]"
            }`}
          >
            {t === "all" ? "Toutes" : t === "paid" ? "Payées" : "En attente"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[var(--oz-gold)]" /></div>
      ) : orders.length === 0 ? (
        <p data-testid="admin-empty" className="text-center text-[var(--oz-muted)] py-16">Aucune commande.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[var(--oz-border)] rounded-2xl">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead>
              <tr className="text-left text-[var(--oz-muted)] border-b border-[var(--oz-border)]">
                <th className="px-4 py-3 font-semibold">Commande</th>
                <th className="px-4 py-3 font-semibold">Moyen</th>
                <th className="px-4 py-3 font-semibold">Statut</th>
                <th className="px-4 py-3 font-semibold">Montant</th>
                <th className="px-4 py-3 font-semibold">Transaction</th>
                <th className="px-4 py-3 font-semibold">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, idx) => (
                <tr key={o.order_number || o.transaction_id || idx} data-testid={`admin-order-${o.order_number || idx}`} className="border-b border-[var(--oz-border)] last:border-0">
                  <td className="px-4 py-3 font-semibold text-[var(--oz-text)]">
                    {o.order_number}
                    {o.customer_email && <div className="text-xs font-normal text-[var(--oz-muted)]">{o.customer_email}</div>}
                    {o.invoice_url && (
                      <a href={o.invoice_url} target="_blank" rel="noopener noreferrer" data-testid={`order-invoice-${o.order_number}`} className="block text-xs font-semibold text-[var(--oz-gold)] underline">
                        Facture {o.invoice_number}
                      </a>
                    )}
                    {o.buyer_type === "professionnel" && (
                      <div data-testid={`order-buyer-pro-${o.order_number}`} className="text-xs font-normal text-[var(--oz-muted)]">
                        Pro : {o.company_name} — SIRET {o.siret}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--oz-muted)]">{PROVIDER_LABEL[o.provider] || o.provider}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[o.order_status] || "bg-gray-100 text-gray-600"}`}>
                      {STATUS_LABEL[o.order_status] || o.order_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-[var(--oz-text)]">{o.amount != null ? fmt(o.amount) : "—"}</td>
                  <td className="px-4 py-3 text-xs text-[var(--oz-muted)] max-w-[160px] truncate" title={o.transaction_id || ""}>{o.transaction_id || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[var(--oz-muted)]">
                    {o.created_at ? new Date(o.created_at).toLocaleString("fr-FR") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
